// src/admin/AdminReferralRebalance.js
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AdminPageWrapper from './components/AdminPageWrapper';
import api from '../utils/api';
import { API } from '../config/apiConfig';

function DetailRow({ label, value }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value ?? '—'}</Typography>
    </Box>
  );
}

export default function AdminReferralRebalance() {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [stats, setStats] = useState({ parentsWithTooMany: 0, usersToReallocate: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const url = API?.adminRebalanceStats ?? '/api/admin/rebalance/stats';
      const res = await api.get(url);
      const data = res?.data;
      if (process.env.NODE_ENV === 'development') console.log('[Rebalance] stats response', data);
      if (data?.success) {
        setStats({
          parentsWithTooMany: data.parentsWithTooMany ?? 0,
          usersToReallocate: data.usersToReallocate ?? 0,
        });
      } else {
        setStats({ parentsWithTooMany: 0, usersToReallocate: 0 });
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[Rebalance] stats failed', e?.response?.status, e?.response?.data);
      setStats({ parentsWithTooMany: 0, usersToReallocate: 0 });
    }
  }, []);

  const fetchNext = useCallback(async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const url = API?.adminRebalanceNext ?? '/api/admin/rebalance/next';
      const res = await api.get(url);
      const data = res?.data;
      if (process.env.NODE_ENV === 'development') console.log('[Rebalance] next response', { success: data?.success, hasCandidate: !!data?.candidate, error: data?.error });
      if (data?.success) {
        setCandidate(data.candidate ?? null);
        if (!loadedOnce) setLoadedOnce(true);
        await fetchStats();
      } else {
        setError(data?.error || 'Failed to fetch next candidate');
        setCandidate(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Request failed';
      const status = err?.response?.status;
      if (process.env.NODE_ENV === 'development') console.warn('[Rebalance] next failed', status, err?.response?.data);
      setError(status === 403 ? 'Admin access required. Log in as admin.' : msg);
      setCandidate(null);
    } finally {
      setLoading(false);
    }
  }, [loadedOnce, fetchStats]);

  const handleApply = async () => {
    if (!candidate?.UserID || !candidate?.ProposedNewParentReferralCode) {
      setError('Missing user or new parent');
      return;
    }
    setError('');
    setSuccess('');
    setApplying(true);
    try {
      const url = API?.adminRebalanceApply ?? '/api/admin/rebalance/apply';
      await api.post(url, {
        userId: candidate.UserID,
        newReferredByCode: candidate.ProposedNewParentReferralCode,
      });
      setSuccess('Reallocation applied. Fetching next…');
      setCandidate(null);
      await fetchStats();
      await fetchNext();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Apply failed';
      setError(msg);
    } finally {
      setApplying(false);
    }
  };

  React.useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <AdminPageWrapper title="Referral tree rebalance" subtitle="Fix users with more than 2 direct referrals (one at a time)">
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

          <Alert severity={stats.usersToReallocate > 0 ? 'warning' : 'info'} sx={{ mb: 1 }}>
            <strong>Status:</strong> {stats.parentsWithTooMany} parent(s) have more than 2 direct children.{' '}
            <strong>{stats.usersToReallocate} user(s)</strong> still need reallocation.
            {stats.usersToReallocate > 0 && ' Keep clicking "Get next candidate" and "Apply" until this reaches 0.'}
            {stats.parentsWithTooMany === 0 && stats.usersToReallocate === 0 && (
              <Typography component="span" display="block" sx={{ mt: 1, fontSize: '0.85rem' }}>
                If SSMS shows data but this shows 0, the API may be using a different database. Use <code>npm run start:local</code> and ensure the API .env.local points to the same DB as SSMS.
              </Typography>
            )}
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={fetchNext}
              disabled={loading}
            >
              {loadedOnce ? 'Refresh next' : 'Get next candidate'}
            </Button>
            <Button variant="outlined" size="small" onClick={fetchStats}>Refresh stats</Button>
          </Box>

          {loading && !candidate && !loadedOnce && (
            <Typography color="text.secondary">Loading…</Typography>
          )}

          {!loading && loadedOnce && !candidate && (
            <Alert severity="info">
              {stats.usersToReallocate === 0
                ? 'No more users to reallocate. Tree is balanced.'
                : `No candidate returned but ${stats.usersToReallocate} user(s) still need reallocation. You may be hitting "No available slot" for their trees (full within 20 levels). Check the browser console or API for errors.`}
            </Alert>
          )}

          {candidate && (
            <Card variant="outlined" sx={{ mt: 1, overflow: 'visible' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Change placement: parent A → parent B
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The user below will be moved so their placement parent changes from <strong>Parent A</strong> (current) to <strong>Parent B</strong> (new). User and referral link stay the same; only the tree position changes.
                </Typography>

                {/* User being moved */}
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <PersonIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>User to reallocate</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <DetailRow label="User ID" value={candidate.UserID} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <DetailRow label="Username" value={candidate.UserName} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <DetailRow label="Email" value={candidate.Email} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <DetailRow label="Referral code" value={candidate.ReferralCode} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Parent A → Parent B */}
                <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ height: '100%', borderColor: 'error.light', borderWidth: 1 }}>
                      <CardContent>
                        <Chip label="Parent A (current)" size="small" color="error" sx={{ mb: 1.5 }} />
                        <DetailRow label="Username" value={candidate.CurrentParentUserName} />
                        <DetailRow label="Referral code" value={candidate.CurrentParentReferralCode} />
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          Current placement parent — will be removed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowForwardIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ height: '100%', borderColor: 'success.main', borderWidth: 1 }}>
                      <CardContent>
                        <Chip label="Parent B (new)" size="small" color="success" sx={{ mb: 1.5 }} />
                        <DetailRow label="Username" value={candidate.ProposedNewParentUserName} />
                        <DetailRow label="Referral code" value={candidate.ProposedNewParentReferralCode} />
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          New placement parent — user will be placed here
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={applying ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleApply}
                  disabled={applying}
                >
                  {applying ? 'Applying…' : 'Apply: change parent from A to B'}
                </Button>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Paper>
    </AdminPageWrapper>
  );
}
